import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { Currency } from "../../../../../../../models/currency";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import CurrencyModal from "./CurrencyModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { useAppState } from "Context/StateContext";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const Currencies = ({ permission }: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const {
    company,
    currencies,
    hasErrors,
    page,
    total,
    pageSize,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createCurrency,
    updateCurrency,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectCurrency, setSelectCurrency] = useState<Currency>();
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [cleanSelection, setCleanSelection] = useState<boolean>(false);
  const {currentUser} = useAppState();

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
    `}
    >
      {status}
    </Badge>
  );

  const editCurrency = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const currencyToUpdate = currencies.find((currency) => Number(currency.id) === Number(index));

    if (currencyToUpdate) {
      setSelectCurrency(currencyToUpdate);
      setOpenModal(true);
    }
  };

  const changeStatusCurrency = (index: number) => {
    const currencyToUpdate = currencies.find((currency) => Number(currency.id) === Number(index));

    if (currencyToUpdate) {
      if (company?.currencyId === Number(index)) {
        NotificationToast.warning(
          translate("thisCurrencyIsAlreadyRelatedToACompanyThereforeYouCannotChangeHisStatus")
        );

        return;
      }
      updateCurrency({
        id: Number(index),
        currency: {
          ...currencyToUpdate,
          status: currencyToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveCurrencySelected = (rowArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowArr, rows, currencies);

    setRows([...rowsSelected]);
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.currencies",
      });
      setHasErrors(undefined);
      setCleanSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div>
      <ShipeeziNewGrid
        data={currencies}
        columns={[
          { name: "code", title: translate("code") },
          { name: "name", title: translate("fullName") },
          { name: "symbol", title: translate("symbol") },
          {
            name: "decimalPoints",
            title: translate("decimalPoints"),
            hideSearch: true,
          },
          {
            name: "status",
            title: translate("status"),
            formatComponent: (status: string) => badgeStatus(status),
            hideSearch: true,
          },
        ]}
        totalItems={total}
        loadingData={isLoading}
        emptyData={isLoading ? "Loading..." : translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={cleanSelection}
        handleSelect={(rows: number[]) => handleSaveCurrencySelected(rows)}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (currencyId) => editCurrency(Number(currencyId))
            : undefined,
          handleChange: (currencyId) => changeStatusCurrency(Number(currencyId)),
        }}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleChangeStatus", "handleNewRecord"],
          },
          handleNewRecord: permission && isShowAction(currentUser, permission.create)
            ? {
              text: translate("create"),
              action: () => {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setSelectCurrency(undefined);
                setOpenModal(true);
              },
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: (filters) => {
              handleSearch(filters);
            },
          },
          handleFilterBy: {
            categories: [
              { name: translate("code"), column: "code" },
              {
                name: translate("fullName"),
                column: "name",
              },
              { name: translate("symbol"), column: "symbol" },
              {
                name: translate("decimalPoints"),
                column: "decimalPoints",
              },
              {
                name: translate("status"),
                column: "status",
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
      />
      {openModal && (
        <CurrencyModal
          currencyToUpdate={selectCurrency}
          open={openModal}
          isLoading={isLoading}
          hasError={hasErrors}
          closeModal={() => setOpenModal(false)}
          updateCurrency={(currency: Currency) =>
            currency.id && updateCurrency({ id: currency?.id, currency })
          }
          createCurrency={(currency: Currency) => createCurrency(currency)}
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateCurrency({ id: rowId, currency: { status } as Currency });
              setCleanSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default Currencies;
